import React, { useCallback, useEffect, useRef, useState } from "react";
import Dropzone, { Accept, DropzoneInputProps } from "react-dropzone";
import AvatarEditor from "react-avatar-editor";
import { toast } from "react-toastify";
import { Slider } from "@mui/material";
import { Box, ButtonPrimary, ButtonPrimary1, HStack, VStack } from "../utils";
import { fetchAndConvertToDataURL } from "../../utils/DownloadFile";
import { Label } from "./InputField";

type ImageResizerProps = {
  onCancel: () => void;
  onSave: (
    image?: string,
    orientationSettings?: {
      orientation: "portrait" | "landscape";
      displayFormat: "logoOnly" | "logoAndName" | "nameOnly";
    }
  ) => void;
  imageUrl?: string;
  imageConditions?: {
    width: number;
    height: number;
    size?: number;
  };
  orientationSettings?: {
    show: boolean;
    orientation: "portrait" | "landscape";
    displayFormat: "logoOnly" | "logoAndName" | "nameOnly";
  };
};

export default function ImageResizer(props: ImageResizerProps) {
  const { onCancel, onSave, imageUrl, imageConditions, orientationSettings } =
    props;
  const editorRef = useRef() as React.MutableRefObject<AvatarEditor>;
  const acceptedTypes = ["image/jpeg", "image/png", "image/jpg"];
  const dropzoneAcceptedTypes: Accept = {
    "image/jpeg": [".jpeg", ".png"],
    "image/png": [".jpeg", ".png"],
    "image/jpg": [".jpeg", ".png"],
  };
  const [openImageEdit, setImageEdit] = useState(false);
  const [imageProperties, setImageProperties] = useState({
    image: "",
    width: imageConditions?.width || 250,
    height: imageConditions?.height || 250,
    scale: 1,
    borderRadius: 0,
    showGrid: false,
    rotate: 0,
  });
  const [orientationSettingsState, setOrientationSettings] = useState<{
    orientation: "portrait" | "landscape";
    displayFormat: "logoOnly" | "logoAndName" | "nameOnly";
  }>(
    orientationSettings || {
      orientation: "portrait",
      displayFormat: "logoAndName",
    }
  );
  useEffect(() => {
    if (imageUrl) setImageProperties({ ...imageProperties, image: imageUrl });
  }, [imageUrl]);

  const onDrop = useCallback(
    (acceptedFiles: any) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const fileData = acceptedFiles[0];
        if (!acceptedTypes.includes(fileData.type)) {
          toast("Invalid filetype", { autoClose: 2000, type: "error" });
          return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new window.Image();
          img.onload = () => {
            let inferredOrientation: "portrait" | "landscape" = "portrait";
            if (img.width > img.height) inferredOrientation = "landscape";
            else if (img.height > img.width) inferredOrientation = "portrait";
            else inferredOrientation = "portrait";
            setOrientationSettings((prev) => ({
              ...prev,
              orientation: inferredOrientation,
            }));
            setImageProperties((prev) => ({
              ...prev,
              image: fileData,
            }));
            setImageEdit(true);
          };
          img.src = e.target?.result as string;
        };
        reader.readAsDataURL(fileData);
      }
    },
    [acceptedTypes, imageProperties]
  );
  const setEditorRef = (editor: any) => {
    editorRef.current = editor;
  };
  const handleSubmit = () => {
    let img;
    if (editorRef && editorRef.current !== undefined && openImageEdit) {
      const canvas = editorRef.current?.getImageScaledToCanvas();
      canvas.setAttribute("crossOrigin", "anonymous");
      // avoid tainting the canvas
      img = canvas.toDataURL("image/png");
    }
    onSave(img, orientationSettingsState);
  };
  function handleImageEdit(value: number | number[], property: string): void {
    if (
      typeof value === "number" &&
      property !== "scale" &&
      property !== "rotate"
    ) {
      value = Math.round(value);
      if (
        property === "height" &&
        imageConditions?.height &&
        value > imageConditions.height
      ) {
        return;
      } else if (
        property === "width" &&
        imageConditions?.width &&
        value > imageConditions.width
      ) {
        return;
      }
    }
    setImageProperties({ ...imageProperties, [property]: value });
  }
  useEffect(() => {
    if (orientationSettingsState.orientation === "portrait") {
      const size = imageConditions?.width || 250;
      setImageProperties((prev) => ({
        ...prev,
        width: size,
        height: size,
      }));
    } else {
      setImageProperties((prev) => ({
        ...prev,
        width: imageConditions?.width || 250,
        height: imageConditions?.height || 100,
      }));
    }
  }, [orientationSettingsState.orientation]);
  return (
    <div className="w-full">
      <div className="justify-between px-10 text-lg font-medium py-7">
        <h6 className="flex justify-between border-b">
          Upload and Resize Image
          <button
            type="button"
            onClick={() => onCancel()}
            className="cursor-pointer"
          >
            X
          </button>
        </h6>
        <HStack className="w-full mt-4 gap-4 flex">
          <VStack
            className="min-w-[300px] justify-center mt-4"
            style={{
              width:
                orientationSettingsState.orientation === "portrait"
                  ? `${imageProperties.width}px`
                  : `${imageProperties.width}px`,
              height:
                orientationSettingsState.orientation === "portrait"
                  ? `${imageProperties.height}px`
                  : `${imageProperties.height}px`,
              minWidth: "200px",
              minHeight: "200px",
              maxWidth: "400px",
              maxHeight: "400px",
            }}
          >
            <Dropzone
              accept={dropzoneAcceptedTypes}
              onDrop={onDrop}
              noClick={imageProperties.image !== ""}
              noKeyboard
            >
              {({ getRootProps, getInputProps }) => (
                <div {...getRootProps()}>
                  <AvatarEditor
                    ref={setEditorRef}
                    border={5}
                    {...imageProperties}
                    crossOrigin="anonymous"
                  />
                  <input {...getInputProps()} />
                </div>
              )}
            </Dropzone>
            <span className="text-xxs text-gray-201 mt-4">
              Drag and Drop an image into the box and edit if necessary
            </span>
            {imageConditions && (
              <span className="text-xxs text-gray-201 mt-1">
                * Image must be of {imageConditions.width}x
                {imageConditions.height} pixels
              </span>
            )}
          </VStack>
          <VStack className={`mx-10 ${!openImageEdit && "cursor-not-allowed"}`}>
            <HStack className="flex">
              <Box className="relative w-32">
                <Label className="text-xxs">Zoom</Label>
                <Slider
                  value={imageProperties.scale}
                  shiftStep={0.1}
                  disabled={!openImageEdit}
                  step={0.1}
                  onChange={(_, val) => handleImageEdit(val, "scale")}
                  valueLabelDisplay="on"
                  min={0.1}
                  max={4}
                />
              </Box>
            </HStack>
            <HStack className="flex">
              <Box className="relative w-32">
                <Label className="text-xxs">Rotation</Label>
                <Slider
                  value={imageProperties.rotate}
                  shiftStep={0.1}
                  step={0.1}
                  disabled={!openImageEdit}
                  onChange={(_, val) => handleImageEdit(val, "rotate")}
                  valueLabelDisplay="auto"
                  min={-270}
                  max={270}
                />
              </Box>
            </HStack>
            <HStack className="flex">
              <Box className="relative w-32">
                <Label className="text-xxs">Height</Label>
                <Slider
                  value={imageProperties.height}
                  shiftStep={5}
                  step={5}
                  disabled={!openImageEdit}
                  onChange={(_, val) => handleImageEdit(val, "height")}
                  valueLabelDisplay="auto"
                  min={100}
                  max={250}
                />
              </Box>
            </HStack>
            <HStack className="flex">
              <Box className="relative w-32">
                <Label className="text-xxs">Width</Label>
                <Slider
                  value={imageProperties.width}
                  shiftStep={5}
                  step={5}
                  disabled={!openImageEdit}
                  onChange={(_, val) => handleImageEdit(val, "width")}
                  valueLabelDisplay="auto"
                  min={100}
                  max={250}
                />
              </Box>
            </HStack>
          </VStack>
        </HStack>
        {orientationSettings?.show && (
          <VStack className="mt-6">
            <Label className="text-sm font-medium">
              Image Display Settings
            </Label>
            <HStack className="gap-4 w-full">
              <Box className="w-1/2">
                <Label className="text-xxs">Orientation</Label>
                <select
                  className="form-select mt-1"
                  value={orientationSettingsState.orientation || "portrait"}
                  onChange={(e) =>
                    setOrientationSettings({
                      ...orientationSettingsState,
                      orientation: e.target.value as "portrait" | "landscape",
                    })
                  }
                >
                  <option value="portrait">Portrait</option>
                  <option value="landscape">Landscape</option>
                </select>
              </Box>
              <Box className="w-1/2">
                <Label className="text-xxs">Display Format</Label>
                <select
                  className="form-select mt-1"
                  value={
                    orientationSettingsState.displayFormat || "logoAndName"
                  }
                  onChange={(e) =>
                    setOrientationSettings({
                      ...orientationSettingsState,
                      displayFormat: e.target.value as
                        | "logoOnly"
                        | "logoAndName"
                        | "nameOnly",
                    })
                  }
                >
                  <option value="logoOnly">Logo Only</option>
                  <option value="logoAndName">Logo and Name</option>
                </select>
              </Box>
            </HStack>
          </VStack>
        )}

        <HStack className="justify-end gap-4 mt-8">
          <ButtonPrimary1 onClick={onCancel}>Cancel</ButtonPrimary1>
          <ButtonPrimary onClick={handleSubmit}>Save</ButtonPrimary>
        </HStack>
      </div>
    </div>
  );
}
